import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import DateInput from '../common/DateInput';
import DriverFilters, { useDriverList } from '../common/DriverFilters';
import { tableEmpty } from '../common/PageHeader';
import TableSearch, { filtriraj } from '../common/TableSearch';
import { listiciApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';
import { addMonths, fmtDate, fmtDateTime, startOfToday } from '../../utils/dates';
import { exportXlsx, stampaVremena } from '../../utils/excel';
import { driverLabel } from '../../utils/activities';
import { IZVOR_LABEL } from './plan';

const STATUSI = [
    { label: 'Za izbacivanje', value: 'IZBACITI' },
    { label: 'Izbačeni', value: 'IZBACEN' },
    { label: 'Uneti', value: 'UNET,ZAMENJEN' },
    { label: 'Svi', value: '' },
];
const STATUS_LABEL = { IZBACITI: ['Za izbacivanje', 'warn'], IZBACEN: ['Izbačen', 'neutral'], UNET: ['Unet', 'ok'], ZAMENJEN: ['Zamenjen', 'ok'] };

/* Pregled listića po kompaniji, vozaču i periodu — pre svega oni za izbacivanje (za period već postoji kartica,
   listić ili ručni unos). Operater potvrđuje izbacivanje; spisak se izvozi u Excel. */
export default function ZaIzbacivanje({ active }) {
    const alerts = useAlerts();
    const list = useDriverList([]);
    /* vozač je opcion (podrazumevano svi) — zato sopstveni izbor, a ne vozač iz useDriverList (on uvek bira prvog) */
    const [vozac, setVozac] = useState(null);
    useEffect(() => { if (vozac && !list.drivers.some((d) => d.id === vozac.id)) setVozac(null); }, [list.drivers]); // eslint-disable-line react-hooks/exhaustive-deps
    const [od, setOd] = useState(() => addMonths(startOfToday(), -1));
    const [kraj, setKraj] = useState(() => startOfToday());
    const [status, setStatus] = useState('IZBACITI');
    const [rows, setRows] = useState([]);
    const [sel, setSel] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pretraga, setPretraga] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const res = await listiciApi.list({
                companyIds: list.companyIds, driverId: vozac ? vozac.id : null, from: od, to: kraj,
                statusi: status ? status.split(',') : [],
            });
            setRows(res);
            setSel([]);
        } catch { /* toast */ }
        setLoading(false);
    };
    useEffect(() => { if (active) load(); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

    const oznaci = async () => {
        if (!(await alerts.confirm('Izbacivanje', `Označiti ${sel.length} listić(a) kao izbačene?`))) return;
        try {
            await listiciApi.setStatus(sel.map((r) => r.id), 'IZBACEN');
            alerts.success('Listići su označeni kao izbačeni');
            load();
        } catch { /* toast */ }
    };

    const period = (r) => `${fmtDateTime(r.pocetakLokalno)} – ${fmtDateTime(r.krajLokalno)}`;
    const prikaz = useMemo(() => filtriraj(rows, pretraga, (r) => [r.companyName || 'Bez kompanije', r.driverName, r.fileName, period(r),
        r.izvor && IZVOR_LABEL[r.izvor], r.napomena, (STATUS_LABEL[r.status] || [r.status])[0], fmtDateTime(r.uploadedAt)]), [rows, pretraga]); // eslint-disable-line react-hooks/exhaustive-deps
    const izvoz = () => exportXlsx({
        fileName: `Listici za izbacivanje - ${stampaVremena()}.xlsx`,
        sheetName: 'Listići',
        title: 'Analogni listići',
        subtitle: `Period ${fmtDate(od)} – ${fmtDate(kraj)} · ${STATUSI.find((s) => s.value === status).label}`,
        columns: [
            { header: 'Firma', value: (r) => r.companyName || 'Bez kompanije' },
            { header: 'Vozač', key: 'driverName' },
            { header: 'Listić (fajl)', key: 'fileName' },
            { header: 'Period (Beograd)', value: period },
            { header: 'Već postoji', value: (r) => (r.izvor ? IZVOR_LABEL[r.izvor] : '') },
            { header: 'Postojeće aktivnosti', key: 'napomena', wrap: true, width: 60 },
            { header: 'Status', value: (r) => (STATUS_LABEL[r.status] || [r.status])[0] },
            { header: 'Obrađeno', value: (r) => fmtDateTime(r.uploadedAt) },
        ],
        rows: prikaz,
    }).catch(() => alerts.error('Izvoz u Excel nije uspeo'));

    return (
        <section className="panel" aria-labelledby="izb-title">
            <div className="panel-head">
                <div>
                    <h3 className="panel-title" id="izb-title">Listići za izbacivanje</h3>
                    <p className="panel-sub">Listići za čiji period već postoje aktivnosti (kartica, drugi listić ili ručni unos) — ne unose se, fizički se izbacuju.</p>
                </div>
            </div>
            <DriverFilters list={list} showDriver={false}>
                <div className="field grow">
                    <label htmlFor="izb-voz">Vozač</label>
                    <Dropdown inputId="izb-voz" value={vozac} options={list.drivers.map((d) => ({ label: driverLabel(d), value: d }))} dataKey="id"
                        onChange={(e) => setVozac(e.value)} filter showClear placeholder="Svi vozači" emptyMessage="Nema pronađenih vozača"
                        virtualScrollerOptions={list.drivers.length > 10 ? { itemSize: 40 } : undefined} />
                </div>
                <div className="field date">
                    <label htmlFor="izb-od">Od</label>
                    <DateInput inputId="izb-od" value={od} onChange={(e) => setOd(e.value)} showIcon />
                </div>
                <div className="field date">
                    <label htmlFor="izb-do">Do</label>
                    <DateInput inputId="izb-do" value={kraj} onChange={(e) => setKraj(e.value)} showIcon />
                </div>
                <div className="field">
                    <label htmlFor="izb-st">Status</label>
                    <Dropdown inputId="izb-st" value={status} options={STATUSI} onChange={(e) => setStatus(e.value)} />
                </div>
                <div className="field inline-field">
                    <Button label="Prikaži" icon="pi pi-search" onClick={load} loading={loading} />
                </div>
            </DriverFilters>
            <TableSearch value={pretraga} onChange={setPretraga} placeholder="Pretraga (vozač, listić, kompanija, napomena, status...)" label="Pretraga listića"
                ukupno={rows.length} pronadjeno={prikaz.length} jedinica="listića">
                {sel.length > 0 && <span className="muted table-count">izabrano {sel.length}</span>}
                    <Button label="Označi kao izbačene" icon="pi pi-check-square" outlined disabled={!sel.length || sel.some((r) => r.status !== 'IZBACITI')} onClick={oznaci} />
                    <Button label="Excel" icon="pi pi-file-excel" outlined severity="secondary" disabled={!rows.length} onClick={izvoz} />
            </TableSearch>
            <DataTable value={prikaz} dataKey="id" loading={loading} selection={sel} onSelectionChange={(e) => setSel(e.value)} selectionMode="checkbox"
                size="small" stripedRows paginator rows={25} rowsPerPageOptions={[25, 50, 100]} sortMode="single" removableSort
                rowGroupMode="subheader" groupRowsBy="companyName" rowGroupHeaderTemplate={(r) => <b>{r.companyName || 'Bez kompanije'}</b>}
                emptyMessage={tableEmpty(pretraga.trim() ? 'Nijedan listić ne odgovara pretrazi' : 'Nema listića za izabrane filtere')}>
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                <Column field="driverName" header="Vozač" sortable />
                <Column field="fileName" header="Listić" sortable />
                <Column header="Period (Beograd)" body={period} sortable sortField="pocetakLokalno" />
                <Column header="Već postoji" body={(r) => (r.izvor ? IZVOR_LABEL[r.izvor] : '—')} />
                <Column field="napomena" header="Postojeće aktivnosti" body={(r) => <span className="cell-wrap">{r.napomena || '—'}</span>} />
                <Column header="Status" body={(r) => { const [l, c] = STATUS_LABEL[r.status] || [r.status, 'neutral']; return <span className={`pill ${c}`}>{l}</span>; }} />
                <Column header="Obrađeno" body={(r) => fmtDateTime(r.uploadedAt)} sortable sortField="uploadedAt" />
            </DataTable>
        </section>
    );
}

